import React, { useState } from 'react';
import { ChevronLeft, Check } from 'lucide-react';
import { useBilling } from '../../hooks/useBilling';
import LoadingScreen from './LoadingScreen';
import './screens.css';

const PlanScreen = ({ goToHome, userId }) => {
  const {
    billingData,
    loading,
    subscribe,
    buyCredits,
    manageSubscription,
    changePlan,
    isSubscribing,
    isBuyingCredits,
    isChangingPlan,
    isOpeningPortal
  } = useBilling(userId);

  // 処理中のプランを追跡するローカルステート
  const [subscribingPlan, setSubscribingPlan] = useState(null);
  const [changingToPlan, setChangingToPlan] = useState(null);

  // subscribe関数をラップして処理中のプランを追跡
  const handleSubscribe = async (planName) => {
    setSubscribingPlan(planName);
    try {
      await subscribe(planName);
    } finally {
      setSubscribingPlan(null);
    }
  };

  // changePlan関数をラップして処理中のプランを追跡
  const handleChangePlan = async (planName) => {
    setChangingToPlan(planName);
    try {
      await changePlan(planName);
    } finally {
      setChangingToPlan(null);
    }
  };

  if (loading) {
    return <LoadingScreen text="プラン情報を読み込み中..." />;
  }

  if (!billingData) {
    return (
      <div className="plan-screen">
        <div className="plan-header">
          <button onClick={goToHome} className="plan-back-button">
            <ChevronLeft size={24} />
          </button>
          <h2>プラン選択</h2>
        </div>
        <div className="plan-error">プラン情報を取得できませんでした</div>
      </div>
    );
  }

  const { plan, monthlyQuota, usedThisPeriod, extraCredits, subscription, scheduledPlan } = billingData;
  const remaining = monthlyQuota - usedThisPeriod + extraCredits;

  // セール表示期間（JST: 2026/1/10 23:59 まで）
  // JST(UTC+9) 2026-01-10 23:59:59 は UTC 2026-01-10 14:59:59。
  // 「それ以降は非表示」を厳密にするため、終了時刻は 15:00:00Z を境界（排他的）にする。
  const SALE_END_EXCLUSIVE_UTC_MS = Date.UTC(2026, 0, 10, 15, 0, 0);
  const isSaleActive = Date.now() < SALE_END_EXCLUSIVE_UTC_MS;

  const planNames = {
    trial: 'トライアル',
    light: 'ライトプラン',
    creator: 'クリエイタープラン'
  };

  const statusLabels = {
    active: '有効',
    trialing: 'トライアル期間中',
    past_due: '支払いエラー',
    canceled: 'キャンセル済み',
    expired: '期限切れ'
  };

  // 日付をフォーマット
  const formatDate = (date) => {
    if (!date) return '未設定';
    try {
      // Firestoreの Timestamp オブジェクト、Date オブジェクト、ISO 8601 文字列のいずれかを処理
      let d;
      if (date.toDate) {
        // Firestore Timestamp
        d = date.toDate();
      } else if (typeof date === 'string') {
        // ISO 8601 文字列
        d = new Date(date);
      } else if (date instanceof Date) {
        // Date オブジェクト
        d = date;
      } else {
        console.warn('Unknown date format:', date);
        return '日付形式エラー';
      }

      // Invalid Date チェック
      if (isNaN(d.getTime())) {
        console.warn('Invalid date:', date);
        return '無効な日付';
      }

      return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return '日付エラー';
    }
  };

  // 有料プラン（light/creator）に加入している場合は管理セクションを表示
  const hasActiveSubscription = (plan === 'light' || plan === 'creator') &&
                                 ['active', 'trialing', 'past_due'].includes(subscription?.status);

  // デバッグ用（開発環境のみ）
  if (import.meta.env.DEV) {
    console.log('PlanScreen Debug:', {
      plan,
      subscriptionStatus: subscription?.status,
      hasActiveSubscription,
      billingData
    });
  }

  return (
    <div className="plan-screen">
      {/* ヘッダー */}
      <div className="plan-header">
        <button onClick={goToHome} className="plan-back-button">
          <ChevronLeft size={24} />
        </button>
        <h2>プラン選択</h2>
      </div>

      {/* セール告知（期間限定） */}
      {isSaleActive && (
        <div className="plan-sale-notice">
          <div className="plan-sale-notice-title">🎉 期間限定お年玉セール（〜2026/1/10 23:59 JST）</div>
          <div className="plan-sale-notice-body">
            LINEでクーポン配布中：ライトプラン 定価より30%OFF / クリエイタープラン 定価より50%OFF
          </div>
          <div className="plan-sale-notice-footnote">※追加クレジットはセール対象外</div>
        </div>
      )}

      {/* 現在のプラン */}
      <div className="plan-current">
        <h3>現在のプラン</h3>
        <div className="plan-current-card">
          <div className="plan-current-header">
            <div className="plan-current-name">{planNames[plan]}</div>
            {subscription?.status && plan !== 'trial' && (
              <span className={`plan-status-badge ${subscription.status}`}>
                {statusLabels[subscription.status] || subscription.status}
              </span>
            )}
          </div>
          <div className="plan-current-usage">
            今月の残り: <strong>{remaining}回</strong>
          </div>
          <div className="plan-current-detail">
            使用状況: {usedThisPeriod}/{monthlyQuota}
            {extraCredits > 0 && ` + ${extraCredits}クレジット`}
          </div>
          {subscription?.currentPeriodEnd && (
            <div className="plan-current-period">
              更新日: {formatDate(subscription.currentPeriodEnd)}
            </div>
          )}

          {/* 次回適用予定のプラン */}
          {scheduledPlan && (
            <div className="plan-scheduled-notice">
              📅 次回更新日（{formatDate(subscription?.currentPeriodEnd)}）から<strong>{planNames[scheduledPlan]}</strong>に切り替わります
            </div>
          )}
        </div>

        {/* 支払いエラーの警告 */}
        {subscription?.status === 'past_due' && (
          <div className="plan-error-notice">
            ⚠️ お支払いに問題があります。下記の「支払い情報・プラン変更・解約」ボタンから支払い方法を更新してください。
          </div>
        )}

        {/* 解約予定の通知 */}
        {subscription?.cancelAtPeriodEnd && subscription?.cancelAt && (
          <div className="plan-cancel-notice">
            ⚠️ このプランは <strong>{formatDate(subscription.cancelAt)}</strong> に解約されます。<br />
            それまでは引き続きご利用いただけます。<br />
            <small>※解約を取り消す場合は、下記の「支払い情報・プラン変更・解約」から変更できます。</small>
          </div>
        )}
      </div>

      {/* サブスクリプションプラン */}
      <div className="plan-section">
        <h3>プラン一覧</h3>

        {/* トライアルプラン */}
        <div className="plan-card">
          <div className="plan-card-header">
            <h4>トライアルプラン</h4>
            {plan === 'trial' && <span className="plan-badge">現在のプラン</span>}
          </div>
          <div className="plan-card-price">無料</div>
          <ul className="plan-card-features">
            <li><Check size={16} /> 月5回まで生成できます</li>
            <li><Check size={16} /> 作成したアプリは7日間のみ利用可能</li>
          </ul>
          {plan === 'trial' && (
            <div className="plan-card-current-note">
              現在このプランをご利用中です
            </div>
          )}
        </div>

        {/* ライトプラン */}
        <div className="plan-card">
          <div className="plan-card-header">
            <h4>ライトプラン</h4>
            {plan === 'light' && <span className="plan-badge">契約中</span>}
            {scheduledPlan === 'light' && <span className="plan-badge plan-badge-scheduled">次回適用予定</span>}
          </div>
          <div className="plan-card-price">¥990 <span>/月</span></div>
          {isSaleActive && (
            <div className="plan-sale-coupon-note">
              LINEクーポン配布中（定価より30%OFF）
            </div>
          )}
          <ul className="plan-card-features">
            <li><Check size={16} /> 月30回まで生成できます</li>
            <li><Check size={16} /> 契約中は作成したアプリを制限なく利用可能</li>
            <li><Check size={16} /> アプリのフッター非表示</li>
            <li><Check size={16} /> 契約日から30日ごとに自動更新</li>
          </ul>
          {plan === 'light' ? (
            <div className="plan-card-current-note">
              現在このプランをご利用中です
            </div>
          ) : scheduledPlan === 'light' ? (
            <div className="plan-card-change-note">
              次回更新日（{formatDate(subscription?.currentPeriodEnd)}）から適用されます
            </div>
          ) : hasActiveSubscription ? (
            <button
              className="plan-card-button"
              onClick={() => handleChangePlan('light')}
              disabled={changingToPlan !== null}
            >
              {changingToPlan === 'light' ? '変更中...' : 'このプランに変更'}
            </button>
          ) : (
            <button
              className="plan-card-button"
              onClick={() => handleSubscribe('light')}
              disabled={subscribingPlan !== null}
            >
              {subscribingPlan === 'light' ? '処理中...' : 'このプランを選択'}
            </button>
          )}
        </div>

        {/* クリエイタープラン */}
        <div className="plan-card">
          <div className="plan-card-header">
            <h4>クリエイタープラン</h4>
            {plan === 'creator' && <span className="plan-badge">契約中</span>}
            {scheduledPlan === 'creator' && <span className="plan-badge plan-badge-scheduled">次回適用予定</span>}
          </div>
          <div className="plan-card-price">¥1,990 <span>/月</span></div>
          {isSaleActive && (
            <div className="plan-sale-coupon-note">
              LINEクーポン配布中（定価より50%OFF）
            </div>
          )}
          <ul className="plan-card-features">
            <li><Check size={16} /> 月100回まで生成できます</li>
            <li><Check size={16} /> 契約中は作成したアプリを制限なく利用可能</li>
            <li><Check size={16} /> アプリのフッター非表示</li>
            <li><Check size={16} /> 契約日から30日ごとに自動更新</li>
          </ul>
          {plan === 'creator' ? (
            <div className="plan-card-current-note">
              現在このプランをご利用中です
            </div>
          ) : scheduledPlan === 'creator' ? (
            <div className="plan-card-change-note">
              次回更新日（{formatDate(subscription?.currentPeriodEnd)}）から適用されます
            </div>
          ) : hasActiveSubscription ? (
            <button
              className="plan-card-button"
              onClick={() => handleChangePlan('creator')}
              disabled={changingToPlan !== null}
            >
              {changingToPlan === 'creator' ? '変更中...' : 'このプランに変更'}
            </button>
          ) : (
            <button
              className="plan-card-button"
              onClick={() => handleSubscribe('creator')}
              disabled={subscribingPlan !== null}
            >
              {subscribingPlan === 'creator' ? '処理中...' : 'このプランを選択'}
            </button>
          )}
        </div>
      </div>

      {/* 追加クレジット */}
      <div className="plan-section">
        <h3>追加クレジット</h3>
        <div className="plan-card">
          <div className="plan-card-header">
            <h4>30回分クレジット</h4>
          </div>
          <div className="plan-card-price">¥1,000</div>
          {isSaleActive && (
            <div className="plan-sale-footnote">
              ※セール対象外
            </div>
          )}
          <p className="plan-card-description">
            有効期限なし。必要な時に追加できます。
          </p>
          {plan === 'trial' ? (
            <>
              <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
                ⚠️ 追加クレジットは有料プラン（ライト・クリエイター）にアップグレード後にご購入いただけます。
              </p>
              <button
                className="plan-card-button plan-card-button-secondary"
                disabled={true}
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
              >
                有料プラン限定
              </button>
            </>
          ) : (
            <button
              className="plan-card-button plan-card-button-secondary"
              onClick={buyCredits}
              disabled={isBuyingCredits}
            >
              {isBuyingCredits ? '処理中...' : '購入する'}
            </button>
          )}
        </div>
      </div>

      {/* サブスク管理 */}
      {(hasActiveSubscription || plan === 'light' || plan === 'creator') && (
        <div className="plan-section">
          <h3>サブスクリプション管理</h3>
          <div className="plan-manage-card">
            <p className="plan-manage-description">
              Stripe管理画面では以下の操作が行えます：
            </p>
            <ul className="plan-manage-features">
              <li>• 支払い方法の変更</li>
              <li>• サブスクリプションの解約</li>
              <li>• 解約の取り消し</li>
              <li>• 支払い履歴の確認</li>
            </ul>
            <div className="plan-manage-notice">
              <strong>💡 プラン変更について</strong><br />
              プランを変更する場合は、上記のプラン一覧から「このプランに変更」ボタンをクリックしてください。<br />
              変更は次回課金日（{formatDate(subscription?.currentPeriodEnd)}）から適用されます。<br />
              <small>※今すぐ追加が必要な場合は「追加クレジット」をご利用ください</small>
            </div>
            <button
              className="plan-manage-button"
              onClick={manageSubscription}
              disabled={isOpeningPortal}
            >
              {isOpeningPortal ? '開いています...' : '支払い情報・解約'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanScreen;
